import { Printer, Plus, Receipt, Unlock, Merge } from "lucide-react";
import { formatCurrency } from "../../lib/utils";

const Legend = ({ color, label }) => (
  <span className="flex items-center gap-1.5">
    <span className={`w-2.5 h-2.5 rounded-sm ${color}`} /> {label}
  </span>
);

const STATE = {
  unpaid: { card: "border-red-500/50 bg-red-500/10", badge: "bg-red-600 text-white", label: "NÃO PAGA" },
  paid: { card: "border-emerald-500/50 bg-emerald-500/10", badge: "bg-emerald-600 text-white", label: "PAGA" },
  reserved: { card: "border-amber-500/40 bg-amber-500/10", badge: "bg-amber-500 text-black", label: "RESERVADA" },
  free: { card: "border-white/10 bg-[#262626]", badge: "bg-zinc-700 text-zinc-200", label: "LIVRE" },
};

const btn = "h-8 rounded-md text-xs font-semibold flex items-center justify-center gap-1 transition-colors";

export const POSTablesView = ({ tables, openOrders, onOpenOrder, onSelectFree, onRelease, onReprint, onCloseTable, onMergeOrders }) => (
  <div className="flex-1 min-h-0 overflow-y-auto p-3" data-testid="pos-tables-view">
    <div className="flex flex-wrap items-center gap-4 mb-3 text-[11px] text-zinc-400">
      <Legend color="bg-red-600" label="Não paga (pedido aberto)" />
      <Legend color="bg-emerald-600" label="Paga (fatura emitida)" />
      <Legend color="bg-zinc-600" label="Livre" />
      <Legend color="bg-amber-500" label="Reservada" />
    </div>
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3">
      {tables.map((t) => {
        const tableOrders = openOrders.filter((o) => o.table_id === t.table_id).sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
        const order = tableOrders[0];
        const tableTotal = tableOrders.reduce((s, o) => s + (o.total || 0), 0);
        const state = order ? "unpaid" : t.status === "cleaning" && t.last_invoice_number ? "paid" : t.status === "reserved" ? "reserved" : "free";
        const s = STATE[state];
        return (
          <div key={t.table_id} className={`rounded-xl border p-3 flex flex-col gap-2 transition-colors ${s.card}`} data-testid={`pos-table-card-${t.table_id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-2xl font-black font-mono leading-none text-zinc-100">{t.number}</p>
                <p className="text-[11px] text-zinc-400 truncate">
                  {t.name} · {t.capacity} lug.
                </p>
                {t.waiter_name && <p className="text-[10px] text-zinc-500 truncate">{t.waiter_name}</p>}
              </div>
              <span className={`px-2 py-1 rounded text-[10px] font-black tracking-wide shrink-0 ${s.badge}`} data-testid={`pos-table-status-${t.table_id}`}>
                {order?.offline ? "OFFLINE · NÃO PAGA" : s.label}
              </span>
            </div>
            {state === "unpaid" && (
              <>
                <p className="text-xs text-zinc-300">
                  Pedido <strong>#{order.order_number}</strong> · {order.items.length} itens
                </p>
                <p className="font-mono font-bold text-red-300" data-testid={`pos-table-total-${t.table_id}`}>{formatCurrency(order.total)}</p>
                <div className="grid grid-cols-2 gap-1.5">
                  <button onClick={() => onOpenOrder(order)} className={`${btn} bg-[#2b2b2b] hover:bg-[#363636] text-zinc-100`} data-testid={`pos-table-open-${t.table_id}`}>
                    <Plus className="w-3.5 h-3.5" /> Itens
                  </button>
                  <button onClick={() => onCloseTable(order)} className={`${btn} bg-red-600 hover:bg-red-500 text-white`} data-testid={`pos-table-pay-${t.table_id}`}>
                    <Receipt className="w-3.5 h-3.5" /> Faturar
                  </button>
                </div>
                {tableOrders.length > 1 && (
                  <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-2 space-y-1" data-testid={`pos-table-extra-orders-${t.table_id}`}>
                    <p className="text-[10px] font-bold text-amber-300 uppercase tracking-wide">+{tableOrders.length - 1} outro(s) pedido(s) nesta mesa · total {formatCurrency(tableTotal)}</p>
                    {tableOrders.slice(1).map((o) => (
                      <div key={o.order_id} className="flex items-center justify-between gap-2 text-[11px] text-zinc-200">
                        <span>#{o.order_number} · {o.items.length} itens · <span className="font-mono">{formatCurrency(o.total)}</span></span>
                        <div className="flex gap-1">
                          <button onClick={() => onOpenOrder(o)} className="h-6 px-2 rounded bg-[#2b2b2b] hover:bg-[#363636] text-zinc-100 text-[10px] font-semibold" data-testid={`pos-table-open-order-${o.order_id}`}>Itens</button>
                          <button onClick={() => onCloseTable(o)} className="h-6 px-2 rounded bg-red-600 hover:bg-red-500 text-white text-[10px] font-semibold" data-testid={`pos-table-pay-order-${o.order_id}`}>Faturar</button>
                        </div>
                      </div>
                    ))}
                    <button onClick={() => onMergeOrders(t)} className={`${btn} w-full bg-amber-500 hover:bg-amber-400 text-black mt-1`} data-testid={`pos-table-merge-${t.table_id}`}>
                      <Merge className="w-3.5 h-3.5" /> Juntar num só pedido
                    </button>
                  </div>
                )}
              </>
            )}
            {state === "paid" && (
              <>
                <p className="text-xs text-emerald-200">
                  Fatura <strong data-testid={`pos-table-invoice-${t.table_id}`}>{t.last_invoice_number}</strong>
                </p>
                <p className="font-mono font-bold text-emerald-300">{formatCurrency(t.last_paid_total || 0)}</p>
                <div className="grid grid-cols-2 gap-1.5">
                  <button onClick={() => onReprint(t.last_order_id)} className={`${btn} bg-[#2b2b2b] hover:bg-[#363636] text-zinc-100`} data-testid={`pos-table-reprint-${t.table_id}`}>
                    <Printer className="w-3.5 h-3.5" /> Fatura
                  </button>
                  <button onClick={() => onRelease(t)} className={`${btn} bg-emerald-600 hover:bg-emerald-500 text-white`} data-testid={`pos-release-table-${t.table_id}`}>
                    <Unlock className="w-3.5 h-3.5" /> Libertar
                  </button>
                </div>
              </>
            )}
            {(state === "free" || state === "reserved") && (
              <button
                onClick={() => onSelectFree(t)}
                className={`${btn} bg-emerald-600/20 hover:bg-emerald-600/40 border border-emerald-500/30 text-emerald-300`}
                data-testid={`pos-table-new-${t.table_id}`}
              >
                <Plus className="w-3.5 h-3.5" /> Novo Pedido
              </button>
            )}
          </div>
        );
      })}
    </div>
    {tables.length === 0 && <p className="text-center text-sm text-zinc-500 py-16">Sem mesas configuradas. Crie mesas em Mesas no sistema principal.</p>}
  </div>
);
